import type { Milestone, MilestoneCreate, MilestoneUpdate } from './types';
import { Paths } from '@/store/constants';
import { apiInvoke } from '@/store/helpers/load';
import { makePayloadRequestInit } from '@/lib/api';
import type { APIParams } from '@/store/helpers/types';
import type { InferredContext } from '@/shared/types/context';

export async function loadMilestones({
  api,
  auth,
  criteria,
  limit,
}: {
  criteria: InferredContext;
  limit?: number;
} & APIParams) {
  const params = new URLSearchParams({ criteria: JSON.stringify(criteria) });
  if (limit) {
    params.append('limit', limit.toString());
  }

  const path = `${Paths.milestones}?${params.toString()}`;
  const response = await apiInvoke<LoadMilestonesResponse>(api, path, auth, {
    method: 'GET',
  });

  return response.data;
}

type LoadMilestonesResponse = {
  data: Milestone[];
};

export async function loadProfileMilestones({
  api,
  auth,
  profileId,
}: { profileId: string } & APIParams) {
  const path = `${Paths.milestones}/${profileId}`;
  const res = await apiInvoke<LoadMilestonesResponse>(api, path, auth, {
    method: 'GET',
  });

  return res.data;
}

type CreateMilestoneResponse = { data: Milestone; message: string };

export async function createMilestone({
  api,
  auth,
  milestone,
}: { milestone: MilestoneCreate } & APIParams) {
  const path = `${Paths.milestones}`;
  const payload = milestone.data;
  const response = await apiInvoke<CreateMilestoneResponse>(
    api,
    path,
    auth,
    makePayloadRequestInit(payload, 'POST'),
  );

  return response;
}

type UpdateMilestoneResponse = {
  data: Milestone;
  message: string;
};

export async function updateMilestone({
  api,
  auth,
  milestone,
}: { milestone: Milestone } & APIParams) {
  const path = `${Paths.milestones}/${milestone.id}`;
  const payload: MilestoneUpdate = {
    occurred_at: milestone.data.occurred_at,
  };
  const response = await apiInvoke<UpdateMilestoneResponse>(
    api,
    path,
    auth,
    makePayloadRequestInit(payload, 'PATCH'),
  );

  return response;
}

type DeleteMilestoneResponse = Milestone;

export async function deleteMilestone({
  api,
  auth,
  milestoneId,
}: { milestoneId: string } & APIParams) {
  const path = `${Paths.milestones}/${milestoneId}`;
  const response = await apiInvoke<DeleteMilestoneResponse>(
    api,
    path,
    auth,
    makePayloadRequestInit({}, 'DELETE'),
  );

  return response;
}
